import React, { FC, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Button,
  Chip,
  Checkbox,
  Tabs,
  Tab,
  Pagination,
} from "@mui/material";
import { BaseTransactionDetailsDto } from "../../../../../api-client";
import {
  LastSchriftDetailsDto,
  SEPAExportOptions,
  CreditTransferExportOptions,
  AccountSEPAData,
} from "../types/sepa-export.types";
import { formatDateUI } from "../../../../../helpers/dateFormat";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import SEPAExportDialog from "../dialogs/SEPAExportDialog";
import CreditTransferExportDialog from "../dialogs/CreditTransferExportDialog";
import { DeleteConfirmationDialog } from "../../../Insurance/DirectBilling/common/exports";
import { TransactionsContext } from "../../../../../stores/Transactions/Transactions.provider";
import useStore from "../../../../../helpers/useStore";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { useApi } from "../../../../../api/ApiProvider";
import { useLocationId, useUserId } from "../../../../../stores/User/User.hooks";
import { useDownload } from "../../../../../hooks/useDownload";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

interface LastschriftTransactionTableProps {
  transactions: LastSchriftDetailsDto[];
  onRefresh?: () => void;
  accountId?: string;
}

const LastschriftTransactionTable: FC<LastschriftTransactionTableProps> = ({
  transactions,
  onRefresh,
  accountId: accountIdProp,
}) => {
  const { handleDownloadPdf } = useDownload();

  const locationId = useLocationId();
  const accountId = accountIdProp || locationId;

  // API hooks for document access
  const { documents: documentsApi } = useApi();

  const transactionStore = useStore(TransactionsContext);
  const {
    exportLastSchriftsToSEPA,
    exportCreditTransfersBatch,
    undoSEPAExport,
    undoCreditTransferExport,
    softDeleteLastSchrift,
    bulkSoftDeleteLastSchrifts,
    getAllunexportedLastSchriftTransactionsForAccount,
    getAllUnexportedCreditTransfersForAccount,
    getUnexportedLastSchrifts,
  } = transactionStore;

  // Tab state (0 = Debit unexported, 1 = Credit unexported, 2 = All)
  const [activeTab, setActiveTab] = useState(0);

  // Pagination state (Tab 2 only)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Unexported debit transactions state (Tab 0)
  const [unexportedDebitTransactions, setUnexportedDebitTransactions] = useState<
    BaseTransactionDetailsDto[]
  >([]);
  const [unexportedDebitLoading, setUnexportedDebitLoading] = useState(false);

  // Unexported credit transactions state (Tab 1)
  const [unexportedCreditTransactions, setUnexportedCreditTransactions] = useState<
    BaseTransactionDetailsDto[]
  >([]);
  const [unexportedCreditLoading, setUnexportedCreditLoading] = useState(false);

  // SEPA account data
  const [accountSepaData, setAccountSepaData] = useState<AccountSEPAData[]>([]);
  const [creditAccountSepaData, setCreditAccountSepaData] = useState<AccountSEPAData[]>([]);

  // Selection state - separate for each tab
  const [selectedDebitIds, setSelectedDebitIds] = useState<string[]>([]);
  const [selectedCreditIds, setSelectedCreditIds] = useState<string[]>([]);
  const [selectedExportedIds, setSelectedExportedIds] = useState<string[]>([]);

  // Dialog state
  const [sepaExportDialogOpen, setSEPAExportDialogOpen] = useState(false);
  const [creditExportDialogOpen, setCreditExportDialogOpen] = useState(false);
  const [sepaExportLoading, setSEPAExportLoading] = useState(false);
  const [creditExportLoading, setCreditExportLoading] = useState(false);
  const [sepaUndoLoading, setSEPAUndoLoading] = useState(false);

  // Delete state
  const [deleteLastSchriftDialogOpen, setDeleteLastSchriftDialogOpen] = useState(false);
  const [lastSchriftToDelete, setLastSchriftToDelete] = useState<LastSchriftDetailsDto | null>(
    null,
  );
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Utility: trigger a browser download from a direct URL
  const triggerDownloadFromUrl = (url: string, filename?: string) => {
    try {
      const a = document.createElement("a");
      a.href = url;
      if (filename) a.download = filename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  // Utility: download given base64 data
  const downloadBase64 = (base64Data: string, mimeType: string, filename: string) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: mimeType || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      triggerDownloadFromUrl(url, filename);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error("Failed to trigger base64 download", err);
    }
  };

  // Dedicated SEPA document download handler (uses Documentation API access fallback)
  const handleDownloadSEPAXml = async (doc: { id: string; name?: string; url?: string }) => {
    try {
      const response = await documentsApi.requestViewAccessForDocument(doc.id);

      if (!response || !(response as any).base64Data) {
        throw new Error("No document data received from server");
      }

      const filename = doc.name || "SEPA.xml";
      downloadBase64(
        (response as any).base64Data,
        (response as any).mimeType || "application/xml",
        filename,
      );
    } catch (error: any) {
      // Special handling: if only a cross-origin URL is available, open it directly to bypass CORS XHR
      if (error?.code === "CROSS_ORIGIN_URL" && error?.url) {
        triggerDownloadFromUrl(error.url, doc.name || "SEPA.xml");
        return;
      }
      console.error("SEPA XML download failed:", error);
      notificationStore.showMessage(
        `Fehler beim Herunterladen der SEPA-Datei: ${error.message || error}`,
        "error",
      );
    }
  };

  // Load account SEPA data for debit transactions (Tab 0)
  const loadAccountSepaData = async () => {
    try {
      const result = await getUnexportedLastSchrifts(accountId);
      setAccountSepaData(result.accountSepaData);
    } catch (error) {
      console.error("Failed to load account SEPA data:", error);
      setAccountSepaData([]);
    }
  };

  // Load account SEPA data for credit transfers (Tab 1)
  const loadCreditAccountSepaData = async () => {
    try {
      const result = await getUnexportedLastSchrifts(accountId);
      setCreditAccountSepaData(result.accountSepaData);
    } catch (error) {
      console.error("Failed to load credit account SEPA data:", error);
      setCreditAccountSepaData([]);
    }
  };

  // Load unexported debit transactions (Tab 0) - filter from all unexported
  const loadUnexportedDebitTransactions = async () => {
    try {
      setUnexportedDebitLoading(true);
      if (!accountId) return;

      const allUnexported = await getAllunexportedLastSchriftTransactionsForAccount(accountId);
      // Filter for debit only: type should be 'Lastschrift', not 'Rückerstattung'
      const debitOnly = allUnexported.filter((tx) => tx.type !== "Rückerstattung");
      setUnexportedDebitTransactions(debitOnly);
    } catch (error) {
      console.error("Failed to load unexported debit transactions:", error);
      setUnexportedDebitTransactions([]);
    } finally {
      setUnexportedDebitLoading(false);
    }
  };

  // Load unexported credit transfer transactions (Tab 1)
  const loadUnexportedCreditTransactions = async () => {
    try {
      setUnexportedCreditLoading(true);
      if (!accountId) return;

      // Use the same endpoint but filter for credit transfers (Rückerstattung type)
      const allUnexported = await getAllunexportedLastSchriftTransactionsForAccount(accountId);
      const creditOnly = allUnexported.filter((tx) => tx.type === "Rückerstattung");
      setUnexportedCreditTransactions(creditOnly);
    } catch (error) {
      console.error("Failed to load unexported credit transactions:", error);
      setUnexportedCreditTransactions([]);
    } finally {
      setUnexportedCreditLoading(false);
    }
  };

  // Helper function: get current transactions based on active tab
  const getCurrentTransactions = (): LastSchriftDetailsDto[] => {
    if (activeTab === 0) {
      // Tab 0: Unexported debit transactions
      return unexportedDebitTransactions.map((tx) => convertBaseToLastSchrift(tx, false));
    } else if (activeTab === 1) {
      // Tab 1: Unexported credit transactions
      return unexportedCreditTransactions.map((tx) => convertBaseToLastSchrift(tx, false));
    } else {
      // Tab 2: All transactions (with pagination)
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return transactions.slice(startIndex, endIndex);
    }
  };

  // Convert BaseTransactionDetailsDto to LastSchriftDetailsDto
  const convertBaseToLastSchrift = (
    tx: BaseTransactionDetailsDto,
    wasExported: boolean,
  ): LastSchriftDetailsDto => {
    return {
      id: tx.id,
      transactionNumber: tx.transactionNumber,
      type: tx.type,
      text: tx.text,
      amount: tx.amount,
      createdAt: tx.createdAt,
      updatedAt: tx.updatedAt || tx.createdAt,
      wasExported,
      exportedOn: null,
      sepaDocument: null,
      tAccounts: [],
      rx: tx.rx
        ? {
            id: "",
            rxNumber: tx.rx.rxNumber || "",
            date: tx.rx.rxDate || "",
            rxDate: tx.rx.rxDate || "",
          }
        : undefined,
      patient: tx.patient
        ? {
            id: String(tx.patient.id || 0),
            firstName: tx.patient.firstName || "",
            lastName: tx.patient.lastName || "",
            email: "",
          }
        : undefined,
      user: tx.user
        ? {
            id: "",
            firstName: tx.user.firstName || "",
            lastName: tx.user.lastName || "",
            email: "",
          }
        : undefined,
      account: tx.account,
      docDto: tx.docDto,
    } as unknown as LastSchriftDetailsDto;
  };

  // Load data when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 0) {
      loadUnexportedDebitTransactions();
      loadAccountSepaData();
    } else if (activeTab === 1) {
      loadUnexportedCreditTransactions();
      loadCreditAccountSepaData();
    }
    // Tab 2 uses the `transactions` prop passed from parent, no separate loading
  }, [activeTab, accountId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Clear all selections when switching tabs
    setSelectedDebitIds([]);
    setSelectedCreditIds([]);
    setSelectedExportedIds([]);
    // Reset pagination when switching to Tab 2
    if (newValue === 2) {
      setCurrentPage(1);
    }
  };

  // Get current selected IDs based on active tab
  const getCurrentSelectedIds = (): string[] => {
    if (activeTab === 0) return selectedDebitIds;
    if (activeTab === 1) return selectedCreditIds;
    return selectedExportedIds;
  };

  // Selection handlers for Tab 0 (Debit unexported)
  const handleSelectDebit = (transactionId: string, checked: boolean) => {
    if (checked) {
      setSelectedDebitIds((prev) => [...prev, transactionId]);
    } else {
      setSelectedDebitIds((prev) => prev.filter((id) => id !== transactionId));
    }
  };

  const handleSelectAllDebit = (checked: boolean) => {
    if (checked) {
      const allIds = unexportedDebitTransactions.map((t) => t.id);
      setSelectedDebitIds(allIds);
    } else {
      setSelectedDebitIds([]);
    }
  };

  // Selection handlers for Tab 1 (Credit unexported)
  const handleSelectCredit = (transactionId: string, checked: boolean) => {
    if (checked) {
      setSelectedCreditIds((prev) => [...prev, transactionId]);
    } else {
      setSelectedCreditIds((prev) => prev.filter((id) => id !== transactionId));
    }
  };

  const handleSelectAllCredit = (checked: boolean) => {
    if (checked) {
      const allIds = unexportedCreditTransactions.map((t) => t.id);
      setSelectedCreditIds(allIds);
    } else {
      setSelectedCreditIds([]);
    }
  };

  // Selection handlers for Tab 2 (All transactions - exported only)
  const handleSelectExported = (transactionId: string, checked: boolean) => {
    if (checked) {
      setSelectedExportedIds((prev) => [...prev, transactionId]);
    } else {
      setSelectedExportedIds((prev) => prev.filter((id) => id !== transactionId));
    }
  };

  const handleSelectAllExported = (checked: boolean) => {
    if (checked) {
      const exportedIds = getCurrentTransactions()
        .filter((t) => t.wasExported)
        .map((t) => t.id);
      setSelectedExportedIds(exportedIds);
    } else {
      setSelectedExportedIds([]);
    }
  };

  // Debit SEPA export handlers (Tab 0)
  const handleOpenDebitExportDialog = () => {
    if (selectedDebitIds.length === 0) {
      notificationStore.showMessage(
        "Bitte wählen Sie mindestens eine Lastschrift-Transaktion aus",
        "warning",
      );
      return;
    }
    setSEPAExportDialogOpen(true);
  };

  const handleDebitSEPAExport = async (exportOptions: SEPAExportOptions) => {
    setSEPAExportLoading(true);
    try {
      const result = await exportLastSchriftsToSEPA(selectedDebitIds, exportOptions);
      setSEPAExportDialogOpen(false);
      setSelectedDebitIds([]);
      onRefresh?.();
      loadUnexportedDebitTransactions(); // Refresh Tab 0

      // Auto-download if possible
      if (result?.downloadUrl) {
        triggerDownloadFromUrl(result.downloadUrl, result.fileName || "SEPA_Lastschrift.xml");
      } else if (result?.documentId) {
        await handleDownloadSEPAXml({
          id: result.documentId,
          name: result.fileName,
        });
      }
    } catch (error) {
      console.error("Debit SEPA export failed:", error);
    } finally {
      setSEPAExportLoading(false);
    }
  };

  // Credit transfer export handlers (Tab 1)
  const handleOpenCreditExportDialog = () => {
    if (selectedCreditIds.length === 0) {
      notificationStore.showMessage(
        "Bitte wählen Sie mindestens eine Rückerstattungs-Transaktion aus",
        "warning",
      );
      return;
    }
    setCreditExportDialogOpen(true);
  };

  const handleCreditTransferExport = async (
    exportOptions: Omit<CreditTransferExportOptions, "creationDateTime">,
  ) => {
    setCreditExportLoading(true);
    try {
      const result = await exportCreditTransfersBatch(selectedCreditIds, exportOptions);
      setCreditExportDialogOpen(false);
      setSelectedCreditIds([]);
      onRefresh?.();
      loadUnexportedCreditTransactions(); // Refresh Tab 1

      // Auto-download if possible
      if (result?.downloadUrl) {
        triggerDownloadFromUrl(result.downloadUrl, result.fileName || "SEPA_Rueckerstattung.xml");
      } else if (result?.documentId) {
        await handleDownloadSEPAXml({
          id: result.documentId,
          name: result.fileName,
        });
      }
    } catch (error) {
      console.error("Credit transfer export failed:", error);
    } finally {
      setCreditExportLoading(false);
    }
  };

  // Undo SEPA export (Tab 2 for exported transactions)
  const handleUndoSEPAExport = async () => {
    if (selectedExportedIds.length === 0) {
      notificationStore.showMessage(
        "Bitte wählen Sie mindestens eine exportierte Transaktion aus",
        "warning",
      );
      return;
    }

    setSEPAUndoLoading(true);
    try {
      // Detect transaction type: check if any selected transaction has creditPermissionId
      const selectedTransactions = getCurrentTransactions().filter((t) =>
        selectedExportedIds.includes(t.id),
      );

      // Separate debit and credit transactions
      const debitIds: string[] = [];
      const creditIds: string[] = [];

      selectedTransactions.forEach((tx) => {
        // Check if it's a credit transfer (has creditPermissionId or type is REFUND)
        const isCreditTransfer = tx.type === "Rückerstattung" || (tx as any).creditPermissionId;
        if (isCreditTransfer) {
          creditIds.push(tx.id);
        } else {
          debitIds.push(tx.id);
        }
      });

      // Call appropriate undo methods
      if (debitIds.length > 0) {
        await undoSEPAExport(debitIds);
      }
      if (creditIds.length > 0) {
        await undoCreditTransferExport(creditIds);
      }

      setSelectedExportedIds([]);
      onRefresh?.();
    } catch (error) {
      console.error("SEPA undo failed:", error);
    } finally {
      setSEPAUndoLoading(false);
    }
  };

  // LastSchrift Delete handlers
  const handleOpenDeleteLastSchriftDialog = (lastSchrift: LastSchriftDetailsDto) => {
    setLastSchriftToDelete(lastSchrift);
    setDeleteLastSchriftDialogOpen(true);
  };

  const handleDeleteLastSchriftCancel = () => {
    setDeleteLastSchriftDialogOpen(false);
    setLastSchriftToDelete(null);
  };

  const handleDeleteLastSchriftConfirm = async () => {
    if (!lastSchriftToDelete) return;

    try {
      await softDeleteLastSchrift(lastSchriftToDelete.id, true); // true = also delete associated Rechnung
      setDeleteLastSchriftDialogOpen(false);
      setLastSchriftToDelete(null);
      onRefresh?.();

      // Refresh debit transactions on Tab 0
      if (activeTab === 0) {
        loadUnexportedDebitTransactions();
      }
    } catch (error) {
      console.error("Error deleting LastSchrift:", error);
      notificationStore.showMessage("Fehler beim Löschen der LastSchrift-Transaktion", "error");
    }
  };

  const handleOpenBulkDeleteDialog = () => {
    const selectedIds = getCurrentSelectedIds();
    if (selectedIds.length === 0) {
      notificationStore.showMessage("Bitte wählen Sie mindestens eine Transaktion aus", "warning");
      return;
    }
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleBulkDeleteConfirm = async () => {
    const selectedIds = getCurrentSelectedIds();
    if (selectedIds.length === 0) return;

    try {
      await bulkSoftDeleteLastSchrifts(selectedIds, true); // true = also delete associated Rechnungen
      setBulkDeleteDialogOpen(false);

      // Clear selections based on active tab
      if (activeTab === 0) {
        setSelectedDebitIds([]);
        loadUnexportedDebitTransactions();
      } else if (activeTab === 1) {
        setSelectedCreditIds([]);
        loadUnexportedCreditTransactions();
      } else {
        setSelectedExportedIds([]);
      }

      onRefresh?.();
    } catch (error) {
      console.error("Error bulk deleting transactions:", error);
      notificationStore.showMessage("Fehler beim Löschen der Transaktionen", "error");
    }
  };

  // Get selected transactions based on active tab (convert to LastSchriftDetailsDto)
  const selectedDebitTransactions: LastSchriftDetailsDto[] = unexportedDebitTransactions
    .filter((t) => selectedDebitIds.includes(t.id))
    .map((t) => convertBaseToLastSchrift(t, false));

  const selectedCreditTransactions: LastSchriftDetailsDto[] = unexportedCreditTransactions
    .filter((t) => selectedCreditIds.includes(t.id))
    .map((t) => convertBaseToLastSchrift(t, false));

  const headers = [
    "Auswahl",
    "Transaktion-Nr.",
    "Rezept-Nr.",
    "Rezeptdatum",
    "Patient",
    "Motiv",
    "Typ",
    "Erstellt von",
    "Mandant",
    "Erstellt am",
    "Preis (€)",
    "Exportiert",
    "Export-Datum",
    "Dokumentieren",
    "Aktionen",
  ];

  const renderRow = (item: LastSchriftDetailsDto) => {
    const lastschriftItem = item;

    // Determine selection state and handlers based on active tab
    const getSelectionState = () => {
      if (activeTab === 0) {
        // Tab 0: Debit unexported
        return {
          isChecked: selectedDebitIds.includes(lastschriftItem.id),
          handleChange: (checked: boolean) => handleSelectDebit(lastschriftItem.id, checked),
        };
      } else if (activeTab === 1) {
        // Tab 1: Credit unexported
        return {
          isChecked: selectedCreditIds.includes(lastschriftItem.id),
          handleChange: (checked: boolean) => handleSelectCredit(lastschriftItem.id, checked),
        };
      } else {
        // Tab 2: All transactions - only exported can be selected
        return {
          isChecked:
            lastschriftItem.wasExported && selectedExportedIds.includes(lastschriftItem.id),
          handleChange: (checked: boolean) => handleSelectExported(lastschriftItem.id, checked),
        };
      }
    };

    const { isChecked, handleChange } = getSelectionState();

    return (
      <TableRow key={item.transactionNumber}>
        <TableCell sx={{ padding: "4px", fontSize: 13 }}>
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={(e) => handleChange(e.target.checked)}
            disabled={activeTab === 2 && !lastschriftItem.wasExported}
          />
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {item.transactionNumber}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {item.rx ? item.rx.rxNumber : ""}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {(item.rx as any)?.rxDate ? formatDateUI((item.rx as any).rxDate) : "Keine Datum"}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {item.patient ? `${item.patient.lastName}, ${item.patient.firstName}` : ""}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>{item.text}</TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {/* Add badge for transaction type */}
          {item.type === "Rückerstattung" ? (
            <Chip label={item.type} color="success" size="small" variant="outlined" />
          ) : item.type === "Lastschrift" ? (
            <Chip label={item.type} color="primary" size="small" variant="outlined" />
          ) : (
            item.type
          )}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {item.user ? `${item.user.lastName}, ${item.user.firstName}` : ""}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {item.account ? item.account.name : ""}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {formatDateUI(item.createdAt)}
        </TableCell>
        <TableCell
          sx={{
            padding: "4px 4px 4px 8px",
            color: item.amount < 0 ? "red" : "inherit",
            fontSize: 13,
          }}
        >
          {item.amount < 0
            ? `(${Math.abs(Number(item.amount)).toFixed(2).replace(".", ",")} €)`
            : `${Number(item.amount).toFixed(2).replace(".", ",")} €`}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          <Chip
            label={item.wasExported ? "Exportiert" : "Nicht exportiert"}
            color={item.wasExported ? "success" : "default"}
            size="small"
          />
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {lastschriftItem.exportedOn ? formatDateUI(lastschriftItem.exportedOn) : "-"}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          {(item as any).docDto && (
            <IconButton size="small" onClick={() => handleDownloadPdf((item as any).docDto!.id)}>
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          )}
          {item.sepaExportPdf && (
            <IconButton size="small" onClick={() => handleDownloadPdf(item.sepaExportPdf!.id)}>
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          )}
          {lastschriftItem.sepaDocument && (
            <IconButton
              size="small"
              onClick={() =>
                handleDownloadSEPAXml({
                  id: lastschriftItem.sepaDocument!.id,
                  name: lastschriftItem.sepaDocument!.name,
                  url: (lastschriftItem.sepaDocument as any).url,
                })
              }
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ padding: "4px 4px 4px 8px", fontSize: 13 }}>
          <IconButton
            onClick={() => handleOpenDeleteLastSchriftDialog(lastschriftItem)}
            size="small"
            title="LastSchrift-Transaktion löschen"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const sortedTransactions = getCurrentTransactions().sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      {/* Tabs */}
      <Box sx={{ mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="transaction tabs"
          sx={{
            minHeight: "42px",
            "& .MuiTab-root": {
              minHeight: "42px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              py: 0.75,
              px: 2,
            },
            "& .Mui-selected": {
              fontWeight: 600,
            },
          }}
          TabIndicatorProps={{
            style: {
              height: "3px",
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={`Lastschrift`}
            icon={<span style={{ fontSize: "0.7rem", opacity: 0.7 }}>nicht exportiert</span>}
            iconPosition="end"
          />
          <Tab
            label={`Rückerstattung`}
            icon={<span style={{ fontSize: "0.7rem", opacity: 0.7 }}>nicht exportiert</span>}
            iconPosition="end"
          />
          <Tab label={`Alle Transaktionen`} />
        </Tabs>
      </Box>

      {/* SEPA Export Toolbar */}
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          mb: 1.5,
          background: "linear-gradient(135deg, #e3ebf2 0%, #d4dfe8 100%)",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            width: "100%",
          }}
        >
          {/* Center Section - Title */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#000000",
              }}
            >
              {activeTab === 0 && "Lastschrift Export"}
              {activeTab === 1 && "Rückerstattung Export"}
              {activeTab === 2 && "Export Verwaltung"}
            </Typography>
          </Box>

          {/* Right Section - Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Export Debit Button - Tab 0 only */}
            {activeTab === 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<UploadIcon fontSize="small" />}
                onClick={handleOpenDebitExportDialog}
                disabled={selectedDebitIds.length === 0 || sepaExportLoading}
                size="small"
                title="Ausgewählte Lastschrift-Transaktionen exportieren"
                sx={{
                  fontSize: "0.8125rem",
                  py: 0.5,
                  px: 1.5,
                }}
              >
                {sepaExportLoading ? "Exportiere..." : `Export (${selectedDebitIds.length})`}
              </Button>
            )}

            {/* Export Credit Button - Tab 1 only */}
            {activeTab === 1 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<UploadIcon fontSize="small" />}
                onClick={handleOpenCreditExportDialog}
                disabled={selectedCreditIds.length === 0 || creditExportLoading}
                size="small"
                title="Ausgewählte Rückerstattungs-Transaktionen exportieren"
                sx={{
                  fontSize: "0.8125rem",
                  py: 0.5,
                  px: 1.5,
                }}
              >
                {creditExportLoading ? "Exportiere..." : `Export (${selectedCreditIds.length})`}
              </Button>
            )}

            {/* Undo Button - Tab 2 only (for exported transactions) */}
            {activeTab === 2 && (
              <Button
                variant="contained"
                color="warning"
                startIcon={
                  <FileDownloadIcon fontSize="small" style={{ transform: "rotate(180deg)" }} />
                }
                onClick={handleUndoSEPAExport}
                disabled={selectedExportedIds.length === 0 || sepaUndoLoading}
                size="small"
                title="Rückgängigmachen: Exportierte Transaktionen können erneut exportiert werden"
                sx={{
                  fontSize: "0.8125rem",
                  py: 0.5,
                  px: 1.5,
                }}
              >
                {sepaUndoLoading ? "Rückgängig..." : `Undo (${selectedExportedIds.length})`}
              </Button>
            )}

            {/* Delete Button - All tabs */}
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={handleOpenBulkDeleteDialog}
              disabled={getCurrentSelectedIds().length === 0}
              size="small"
              title="Ausgewählte Transaktionen löschen"
              sx={{
                fontSize: "0.8125rem",
                py: 0.5,
                px: 1.5,
              }}
            >
              Löschen ({getCurrentSelectedIds().length})
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper} className="scrollable" sx={{ maxHeight: "650px" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.813rem",
                    py: 1,
                    backgroundColor: "#f5f5f5",
                  }}
                  className="table-header-cell-rechnung"
                >
                  {/* Special handling for selection header */}
                  {idx === 0 && header === "Auswahl" ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {/* Tab 0: Debit unexported - single checkbox */}
                      {activeTab === 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Checkbox
                            size="small"
                            indeterminate={
                              selectedDebitIds.length > 0 &&
                              selectedDebitIds.length < unexportedDebitTransactions.length
                            }
                            checked={
                              selectedDebitIds.length > 0 &&
                              selectedDebitIds.length === unexportedDebitTransactions.length
                            }
                            onChange={(e) => handleSelectAllDebit(e.target.checked)}
                            title="Alle Lastschrift-Transaktionen auswählen"
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                          >
                            Alle
                          </Typography>
                        </Box>
                      )}
                      {/* Tab 1: Credit unexported - single checkbox */}
                      {activeTab === 1 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Checkbox
                            size="small"
                            indeterminate={
                              selectedCreditIds.length > 0 &&
                              selectedCreditIds.length < unexportedCreditTransactions.length
                            }
                            checked={
                              selectedCreditIds.length > 0 &&
                              selectedCreditIds.length === unexportedCreditTransactions.length
                            }
                            onChange={(e) => handleSelectAllCredit(e.target.checked)}
                            title="Alle Rückerstattungs-Transaktionen auswählen"
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                          >
                            Alle
                          </Typography>
                        </Box>
                      )}
                      {/* Tab 2: All transactions - checkbox for exported only */}
                      {activeTab === 2 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Checkbox
                            size="small"
                            indeterminate={
                              selectedExportedIds.length > 0 &&
                              selectedExportedIds.length <
                                getCurrentTransactions().filter((t) => t.wasExported).length
                            }
                            checked={
                              selectedExportedIds.length > 0 &&
                              selectedExportedIds.length ===
                                getCurrentTransactions().filter((t) => t.wasExported).length
                            }
                            onChange={(e) => handleSelectAllExported(e.target.checked)}
                            title="Alle exportierten Transaktionen auswählen"
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            Exp
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Tab 0: Debit unexported loading */}
            {activeTab === 0 && unexportedDebitLoading ? (
              <TableRow>
                <TableCell colSpan={headers.length}>
                  <Typography align="center">Lade Lastschrift-Transaktionen...</Typography>
                </TableCell>
              </TableRow>
            ) : activeTab === 1 && unexportedCreditLoading ? (
              /* Tab 1: Credit unexported loading */
              <TableRow>
                <TableCell colSpan={headers.length}>
                  <Typography align="center">Lade Rückerstattungs-Transaktionen...</Typography>
                </TableCell>
              </TableRow>
            ) : sortedTransactions && sortedTransactions.length > 0 ? (
              sortedTransactions.map((transaction) => renderRow(transaction))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length}>
                  <Typography align="center">
                    {activeTab === 0
                      ? "Keine nicht exportierten Lastschrift-Transaktionen gefunden."
                      : activeTab === 1
                        ? "Keine nicht exportierten Rückerstattungs-Transaktionen gefunden."
                        : "Für die ausgewählten Daten wurden keine Transaktionen gefunden."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination - Only for Tab 2 (All Transactions) */}
      {activeTab === 2 && transactions.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={Math.ceil(transactions.length / itemsPerPage)}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* SEPA Debit Export Dialog (Tab 0) */}
      <SEPAExportDialog
        open={sepaExportDialogOpen}
        onClose={() => setSEPAExportDialogOpen(false)}
        selectedTransactions={selectedDebitTransactions}
        accountSepaData={accountSepaData}
        onExport={handleDebitSEPAExport}
        loading={sepaExportLoading}
      />

      {/* Credit Transfer Export Dialog (Tab 1) */}
      <CreditTransferExportDialog
        open={creditExportDialogOpen}
        onClose={() => setCreditExportDialogOpen(false)}
        selectedTransactions={selectedCreditTransactions}
        accountSepaData={creditAccountSepaData}
        onExport={handleCreditTransferExport}
        loading={creditExportLoading}
      />

      {/* LastSchrift Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteLastSchriftDialogOpen}
        onCancel={handleDeleteLastSchriftCancel}
        onConfirm={handleDeleteLastSchriftConfirm}
        title="LastSchrift-Transaktion löschen?"
        content={`Möchten Sie die LastSchrift-Transaktion "${lastSchriftToDelete?.transactionNumber}" wirklich löschen? Die zugehörige Rechnung wird ebenfalls gelöscht und kann später neu erstellt werden.`}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={bulkDeleteDialogOpen}
        onCancel={handleBulkDeleteCancel}
        onConfirm={handleBulkDeleteConfirm}
        title={`${getCurrentSelectedIds().length} Transaktionen löschen?`}
        content={`Möchten Sie ${getCurrentSelectedIds().length} ausgewählte Transaktionen wirklich löschen? Die zugehörigen Rechnungen werden ebenfalls gelöscht und können später neu erstellt werden.`}
      />
    </>
  );
};

export default LastschriftTransactionTable;
